/**
 * THG AJAX Color Swap
 *
 * Uses Shopify's Section Rendering API to swap product content
 * when clicking color swatches. All color siblings must be on
 * the same template (same section ID) for this to work —
 * falls back to normal navigation otherwise.
 *
 * Flow:
 *  1. Capture user state (selected size, qty, accordion open/close)
 *  2. Fetch new section HTML via ?sections=<id>
 *  3. Transplant preserved DOM nodes from old → new
 *  4. Replace section
 *  5. Re-init theme components
 *  6. Restore user state → theme's variant-change cascades
 *     to update price, stock, button, installments
 */
(function () {
  'use strict';

  var swapping = false;
  var cache = {};

  // ─── Selectors ─────────────────────────────────────────────
  var SECTION_SEL   = '.section-main-product';
  var MEDIA_SEL     = '.product-media';
  var SIDEBAR_SEL   = 'sticky-sidebar';
  var SWATCH_SEL    = 'a.product-color-swatches__item';

  // ─── Helpers ───────────────────────────────────────────────

  function getSectionEl() {
    return document.querySelector(SECTION_SEL);
  }

  function getSectionId() {
    var el = getSectionEl();
    if (!el) return null;
    var wrapper = el.closest('.shopify-section');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : null;
  }

  function handleFromHref(href) {
    var m = (href || '').match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  // ─── Prefetch on hover ─────────────────────────────────────

  document.addEventListener('mouseover', function (e) {
    var link = e.target.closest(SWATCH_SEL);
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href || cache[href]) return;

    var sectionId = getSectionId();
    if (!sectionId) return;

    var url = new URL(href, location.origin);
    url.searchParams.set('sections', sectionId);

    cache[href] = fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }, { passive: true });

  // ─── Intercept clicks ──────────────────────────────────────

  document.addEventListener('click', function (e) {
    var link = e.target.closest(SWATCH_SEL);
    if (!link) return;

    var section = getSectionEl();
    if (!section) return;

    var href = link.getAttribute('href');
    if (!href) return;

    e.preventDefault();
    if (swapping) return;
    doSwap(href, section);
  });

  // ─── Core swap ─────────────────────────────────────────────

  async function doSwap(url, section) {
    swapping = true;

    var wrapper = section.closest('.shopify-section');
    var sectionId = getSectionId();
    if (!wrapper || !sectionId) { fallback(url); return; }

    try {
      // 1. Capture user state
      var state = captureState(section);

      // 2. Fetch via Section Rendering API
      var json;
      if (cache[url]) {
        json = await cache[url];
        delete cache[url];
      }
      if (!json) {
        var fetchUrl = new URL(url, location.origin);
        fetchUrl.searchParams.set('sections', sectionId);
        var resp = await fetch(fetchUrl.toString(), { credentials: 'same-origin' });
        json = await resp.json();
      }

      var html = json[sectionId];
      if (!html) { fallback(url); return; }

      // Parse the incoming section
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var incoming = tmp.querySelector('#' + wrapper.id);
      if (!incoming) { fallback(url); return; }

      // 3. Transplant preserved elements from current → incoming
      transplant(wrapper, incoming, state);

      // 4. Replace
      wrapper.replaceWith(incoming);

      // 5. Re-init
      activateScripts(incoming);

      if (window.Shopify && window.Shopify.PaymentButton) {
        try { window.Shopify.PaymentButton.init(); } catch (_) {}
      }

      // 6. Restore user state — this triggers the theme's
      //    variant-change handler which cascades to update
      //    price, stock, button, installments, URL param
      restoreState(incoming, state);

      // 7. URL + title
      history.replaceState({ thgSwap: true }, '', url);
      var titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i);
      // Title is in the full page, not the section — update from URL
      var handle = handleFromHref(url);
      if (handle) {
        updateDocumentTitle(handle);
      }

      // 8. Refresh below-the-fold sections
      refreshPageSections(url);

    } catch (err) {
      console.error('[THG Swap]', err);
      fallback(url);
      return;
    } finally {
      swapping = false;
    }
  }

  // ─── State capture / restore ───────────────────────────────

  function captureState(section) {
    var state = {};

    // Selected size (and any other option values)
    state.options = [];
    var groups = section.querySelectorAll('.product-variant-picker [data-option-group]');
    groups.forEach(function (group, i) {
      var radio = group.querySelector('input[type="radio"]:checked');
      if (radio) { state.options[i] = radio.value; return; }
      var dropdown = group.querySelector('[data-list-value]');
      if (dropdown) { state.options[i] = dropdown.value; }
    });

    // Quantity
    var qtyInput = section.querySelector('qty-selector input, .qty-selector-wrapper input[name="quantity"]');
    state.qty = qtyInput ? qtyInput.value : '1';

    // Accordion open/close states
    state.accordionStates = [];
    section.querySelectorAll('.product-accordion details, smooth-collapse details').forEach(function (d) {
      state.accordionStates.push(d.open);
    });

    return state;
  }

  function restoreState(section, state) {
    // Restore quantity
    var qtyInput = section.querySelector('qty-selector input, .qty-selector-wrapper input[name="quantity"]');
    if (qtyInput && state.qty) {
      qtyInput.value = state.qty;
    }

    // Restore accordion open/close
    var accordions = section.querySelectorAll('.product-accordion details, smooth-collapse details');
    state.accordionStates.forEach(function (wasOpen, i) {
      if (accordions[i]) accordions[i].open = wasOpen;
    });

    // Restore option selections (most importantly: size)
    // This triggers variant-change → updates price, stock,
    // button state, installments, URL variant param
    if (!state.options.length) return;

    var groups = section.querySelectorAll('.product-variant-picker [data-option-group]');
    groups.forEach(function (group, i) {
      if (state.options[i] == null) return;
      var val = state.options[i];

      // Radios
      var radios = group.querySelectorAll('input[type="radio"]');
      for (var j = 0; j < radios.length; j++) {
        if (radios[j].value === val) {
          radios[j].checked = true;
          radios[j].dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }

      // Dropdown
      var dropdown = group.querySelector('[data-list-value]');
      if (dropdown) {
        dropdown.value = val;
        dropdown.dispatchEvent(new Event('change', { bubbles: true }));
        var activator = group.querySelector('[data-dropdown-activator]');
        if (activator) activator.textContent = val;
      }
    });
  }

  // ─── Transplant ────────────────────────────────────────────
  //
  // Move specific DOM nodes from the current section into the
  // incoming section before the swap. These elements stay live
  // in the DOM — no re-render, no flicker.

  function transplant(current, incoming, state) {
    // Payment installments — prevent Shopify payment_terms
    // JS from re-initializing (causes visible flash)
    transplantBySelector(current, incoming,
      '[data-variant-info="installment_variant_id"]',
      function (el) { return el.closest('div'); }  // the block wrapper
    );
  }

  /**
   * Find an element via selector, walk up to its wrapper via
   * wrapperFn, then swap the incoming wrapper with the live one.
   */
  function transplantBySelector(current, incoming, selector, wrapperFn) {
    var curEl = current.querySelector(selector);
    var newEl = incoming.querySelector(selector);
    if (!curEl || !newEl) return;

    var curWrap = wrapperFn ? wrapperFn(curEl) : curEl;
    var newWrap = wrapperFn ? wrapperFn(newEl) : newEl;
    if (!curWrap || !newWrap) return;

    newWrap.replaceWith(curWrap);
  }

  // ─── Script activation ─────────────────────────────────────

  function activateScripts(root) {
    if (!root) return;
    root.querySelectorAll('script:not([src])').forEach(function (old) {
      var el = document.createElement('script');
      Array.from(old.attributes).forEach(function (a) {
        el.setAttribute(a.name, a.value);
      });
      el.textContent = old.textContent;
      old.parentNode.replaceChild(el, old);
    });
  }

  // ─── Title update ──────────────────────────────────────────

  function updateDocumentTitle(handle) {
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (product) {
        if (product && product.title) {
          document.title = product.title + ' – ' + (window.Shopify.shop || '');
        }
      })
      .catch(function () {});
  }

  // ─── Refresh secondary sections ────────────────────────────

  function refreshPageSections(url) {
    var recs = document.querySelector('product-recommendations');
    if (recs) {
      var handle = handleFromHref(url);
      if (handle) {
        // Update the product-id attribute — the custom element
        // will re-fetch recommendations on next intersection
        var currentPid = recs.getAttribute('product-id');
        fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (p) {
            if (p && p.id && String(p.id) !== currentPid) {
              recs.setAttribute('product-id', p.id);
              if (typeof recs.setupObserver === 'function') recs.setupObserver();
            }
          })
          .catch(function () {});
      }
    }

    // JudgeMe
    if (window.jdgm) {
      try { window.jdgm.customizeBadges(); } catch (_) {}
    }
  }

  // ─── Misc ──────────────────────────────────────────────────

  function fallback(url) {
    swapping = false;
    window.location.href = url;
  }

  window.addEventListener('popstate', function () {
    window.location.reload();
  });

})();
